import { memo } from 'react'

import { useTooltip } from '@nivo/tooltip'
import { scaleLinear } from 'd3-scale'
import { line as d3Line, curveMonotoneX } from 'd3-shape'

function ChartLine({
  data = [],
  metric,
  color,
  xScale,
  innerHeight,
  lineMinValue,
  lineMaxValue,
  renderTooltip,
}) {
  const { showTooltipFromEvent, hideTooltip } = useTooltip()

  if (!data.length || !metric) return null

  const yScale = scaleLinear()
    .domain([lineMinValue, lineMaxValue])
    .range([innerHeight, 0])

  const points = data.map(datum => ({
    x: xScale(datum.date) + xScale.bandwidth() / 2,
    y: yScale(Number(datum[metric]) || 0),
    datum,
  }))

  const path = d3Line().x(point => point.x).y(point => point.y).curve(curveMonotoneX)(points)

  const handleMouseEnter = (event, point) => {
    if (!renderTooltip) return

    showTooltipFromEvent(renderTooltip({
      id: metric,
      value: Number(point.datum[metric]) || 0,
      color,
      indexValue: point.datum.date,
      data: point.datum,
    }), event)
  }

  return <g name="line">
    <path d={path} fill="none" stroke={color} strokeWidth={2} />
    {points.map((point, index) => <circle
      key={index}
      cx={point.x}
      cy={point.y}
      r={4}
      fill={color}
      style={{ cursor: 'pointer' }}
      onMouseEnter={event => handleMouseEnter(event, point)}
      onMouseMove={event => handleMouseEnter(event, point)}
      onMouseLeave={hideTooltip}
    />)}
  </g>
}

export function LineAxis({
  innerWidth,
  innerHeight,
  yScale,
  barMinValue,
  barMaxValue,
  lineMinValue,
  lineMaxValue,
  format,
  label,
  labelOffset,
  textColor,
  axisStroke,
}) {
  const ticks = yScale.ticks()
  const barRange = barMaxValue - barMinValue
  const lineRange = lineMaxValue - lineMinValue

  const getLineValue = barValue => {
    if (!barRange) return lineMinValue
    return lineMinValue + ((barValue - barMinValue) / barRange) * lineRange
  }

  return <g transform={`translate(${innerWidth}, 0)`}>
    {ticks.map(tick => <g key={tick} transform={`translate(0, ${yScale(tick)})`}>
      <line x2={5} stroke={axisStroke} />
      <text x={10} dy="0.32em" fill={textColor} fontSize={11}>
        {format ? format(getLineValue(tick)) : getLineValue(tick)}
      </text>
    </g>)}
    {label && <text
      transform={`translate(${labelOffset}, ${innerHeight / 2}) rotate(90)`}
      textAnchor="middle"
      fill={textColor}
      fontSize={11}
    >
      {label}
    </text>}
  </g>
}

export default memo(ChartLine)
